import { useApi } from '../../api/api-context';
import { useQueryMutation } from '../../api/use-query-mutation';
import { useBudgetId } from '../../budget/budget-store';
import { TransactionType } from './transaction-types';
import { logAndThrowError } from '../../utils/errors';
export const useUpdateTransactionApi = (options) => {
    const api = useApi();
    const budgetId = useBudgetId();
    const queryKey = ['transactions', budgetId];
    // api call
    const updateTransactionApi = async (data) => {
        await api.put('/account/transaction', data);
    };
    // optimistic update
    const onMutate = (oldData, request) => {
        if (!oldData) {
            logAndThrowError('No transactions data found');
        }
        // Create a new array to store the updated transactions
        let updatedTransactions = [...oldData];
        // Find the index of the transaction being updated
        const transactionIndex = updatedTransactions.findIndex((t) => t.id === request.id);
        if (transactionIndex === -1) {
            return oldData; // Transaction not found, return original data
        }
        const originalTransaction = updatedTransactions[transactionIndex];
        // **Case 1: Converting Transfer → Regular Transaction **
        if (originalTransaction.type === TransactionType.TRANSFER && request.type === TransactionType.TRANSACTION) {
            const pairedTransactionId = originalTransaction.transferTransactionId;
            // **Step 1: Update the main transaction first**
            updatedTransactions[transactionIndex] = {
                ...request,
                transferTransactionId: undefined,
                transferAccountId: undefined,
            };
            // **Step 2: Now remove the paired transfer transaction (if exists)**
            if (pairedTransactionId) {
                updatedTransactions = updatedTransactions.filter((t) => t.id !== pairedTransactionId);
            }
            updatedTransactions.sort((a, b) => b.dateEpoch - a.dateEpoch || b.id.localeCompare(a.id));
            return updatedTransactions;
        }
        // Case 2: Converting from Regular Transaction to Transfer
        if (originalTransaction.type === TransactionType.TRANSACTION && request.type === TransactionType.TRANSFER) {
            if (!request.transferTransactionId || !request.transferAccountId) {
                logAndThrowError('Transfer transaction details required');
            }
            // Update the main transaction
            updatedTransactions[transactionIndex] = {
                ...request,
            };
            // Create new paired transaction
            const transferTransaction = {
                ...request,
                id: request.transferTransactionId,
                accountId: request.transferAccountId,
                transferAccountId: request.accountId,
                transferTransactionId: request.id,
                inflow: request.outflow,
                outflow: request.inflow,
            };
            updatedTransactions.push(transferTransaction);
            updatedTransactions.sort((a, b) => b.dateEpoch - a.dateEpoch || b.id.localeCompare(a.id));
            return updatedTransactions;
        }
        // Case 3: Updating existing transfer
        if (request.type === TransactionType.TRANSFER && request.transferTransactionId && request.transferAccountId) {
            // Update the main transaction
            updatedTransactions[transactionIndex] = {
                ...updatedTransactions[transactionIndex],
                ...request,
            };
            // Find and update the paired transaction
            const transferIndex = updatedTransactions.findIndex((t) => t.id === request.transferTransactionId);
            if (transferIndex !== -1) {
                updatedTransactions[transferIndex] = {
                    ...updatedTransactions[transferIndex],
                    dateEpoch: request.dateEpoch,
                    payee: request.payee,
                    notes: request.notes,
                    inflow: request.outflow,
                    outflow: request.inflow,
                    accountId: request.transferAccountId,
                    transferAccountId: request.accountId,
                    transferTransactionId: request.id,
                };
            }
            updatedTransactions.sort((a, b) => b.dateEpoch - a.dateEpoch || b.id.localeCompare(a.id));
            return updatedTransactions;
        }
        // Case 4: Regular transaction update
        updatedTransactions[transactionIndex] = {
            ...updatedTransactions[transactionIndex],
            ...request,
        };
        updatedTransactions.sort((a, b) => b.dateEpoch - a.dateEpoch || b.id.localeCompare(a.id));
        return updatedTransactions;
    };
    return useQueryMutation(updateTransactionApi, queryKey, onMutate, options);
};
